import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function Documents({ token }) {
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ✅ filtros avanzados
  const [filters, setFilters] = useState({
    tipoDocumento: "",
    municipio: "",
    entidad: "",
    autores: "",
    palabrasClave: "",
    yearFrom: "",
    yearTo: "",
    categorias: "", // string con comas
    uploadedBy: "", // opcional (admin/user). institución se ignora en backend
  });

  // ✅ opciones para selects (desde /api/documents/filters)
  const [options, setOptions] = useState({
    tipoDocumento: [],
    municipio: [],
    entidad: [],
    anos: [],
  });

  // ✅ paginación
  const [page, setPage] = useState(1);
  const limit = 10;

  // =========================================================
  // Helpers
  // =========================================================
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: "Bearer " + token } }),
    [token]
  );

  const buildParams = () => {
    // q y scope: si scope != all lo mandamos en el campo específico
    const params = {
      page,
      limit,
    };

    const term = search.trim();
    if (term) {
      if (scope === "all") params.q = term;
      if (scope === "title") params.q = term; // backend ya busca en title con q
      if (scope === "keywords") params.palabrasClave = term;
      if (scope === "entidad") params.entidad = term;
      if (scope === "institucion") params.q = term; // backend también busca por User.name
    }

    // filtros avanzados
    Object.entries(filters).forEach(([k, v]) => {
      const value = (v ?? "").toString().trim();
      if (value) params[k] = value;
    });

    return params;
  };

  const loadDocs = async () => {
    try {
      const res = await axios.get("https://back.dataregion.com.co/api/documents", {
        ...authHeaders,
        params: buildParams(),
      });

      // ✅ backend recomendado: { data, meta }
      const payload = res.data;
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      const m = payload?.meta || { total: rows.length, page: 1, limit };

      setDocs(rows);
      setMeta(m);
    } catch (e) {
      console.error("Error cargando documentos:", e);
      setDocs([]);
      setMeta({ total: 0, page: 1, limit });
    }
  };

  const loadFilterOptions = async () => {
    try {
      const res = await axios.get("https://back.dataregion.com.co/api/documents/filters", authHeaders);
      setOptions({
        tipoDocumento: res.data?.tipoDocumento || [],
        municipio: res.data?.municipio || [],
        entidad: res.data?.entidad || [],
        anos: res.data?.anos || [],
      });
    } catch (e) {
      // si no existe endpoint, no rompemos
      console.warn("No se pudo cargar /api/documents/filters");
    }
  };

  // =========================================================
  // Effects
  // =========================================================
  useEffect(() => {
    if (!token) return;
    loadFilterOptions();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadDocs();
    
  }, [token, page, scope]); // search/filters se aplican con botón "Ir" para no spamear requests

  // =========================================================
  // Actions
  // =========================================================
  const rate = async (id, score) => {
    try {
      await axios.post(
        `https://back.dataregion.com.co/api/documents/rate/${id}`,
        { score },
        authHeaders
      );
      alert("✅ Gracias por su valoración");
    } catch (err) {
      alert("❌ Error al valorar");
    }
  };

  const download = async (id, title) => {
    try {
      const res = await axios.get(
        `https://back.dataregion.com.co/api/documents/download/${id}`,
        { ...authHeaders, responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", title || `documento-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error al descargar:", err);
      alert("❌ No se pudo descargar el documento.");
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadDocs();
  };

  const handleReset = () => {
    setSearch("");
    setScope("all");
    setFilters({
      tipoDocumento: "",
      municipio: "",
      entidad: "",
      autores: "",
      palabrasClave: "",
      yearFrom: "",
      yearTo: "",
      categorias: "",
      uploadedBy: "",
    });
    setPage(1);
    setShowAdvanced(false);
    // recarga limpio
    setTimeout(loadDocs, 0);
  };

  // =========================================================
  // UI Helpers
  // =========================================================
  const showingFrom = meta.total ? (meta.page - 1) * meta.limit + 1 : 0;
  const showingTo = meta.total ? Math.min(meta.page * meta.limit, meta.total) : 0;

  return (
    <div className="dr-search">
      {/* HEADER / BUSCADOR */}
      <div className="dr-search-head">
        <h3 className="dr-search-title">Buscar</h3>

        <div className="dr-search-bar">
          <select
            className="dr-search-select"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value="all">Todo DataRegión</option>
            <option value="title">Título</option>
            <option value="keywords">Palabras clave</option>
            <option value="entidad">Entidad</option>
            <option value="institucion">Institución</option>
          </select>

          <input
            className="dr-search-input"
            placeholder="Escribe tu búsqueda…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />

          <button className="dr-search-go" type="button" onClick={handleSearch}>
            Ir
          </button>

          <button className="dr-search-reset" type="button" onClick={handleReset}>
            Limpiar
          </button>
        </div>

        <div className="dr-search-subbar">
          <button
            type="button"
            className="dr-search-advanced"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            {showAdvanced ? "Ocultar filtros avanzados" : "Mostrar filtros avanzados"}
          </button>
        </div>

        {showAdvanced && (
          <div className="dr-advanced">
            <div className="dr-advanced-grid">
              <div className="dr-field">
                <label>Tipo de documento</label>
                <select
                  value={filters.tipoDocumento}
                  onChange={(e) => setFilters((p) => ({ ...p, tipoDocumento: e.target.value }))}
                >
                  <option value="">— Todos —</option>
                  {options.tipoDocumento.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dr-field">
                <label>Municipio</label>
                <select
                  value={filters.municipio}
                  onChange={(e) => setFilters((p) => ({ ...p, municipio: e.target.value }))}
                >
                  <option value="">— Todos —</option>
                  {options.municipio.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dr-field">
                <label>Entidad</label>
                <select
                  value={filters.entidad}
                  onChange={(e) => setFilters((p) => ({ ...p, entidad: e.target.value }))}
                >
                  <option value="">— Todas —</option>
                  {options.entidad.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dr-field">
                <label>Autores</label>
                <input
                  value={filters.autores}
                  placeholder="Ej: Mejía, Luis"
                  onChange={(e) => setFilters((p) => ({ ...p, autores: e.target.value }))}
                />
              </div>

              <div className="dr-field">
                <label>Palabras clave</label>
                <input
                  value={filters.palabrasClave}
                  placeholder="Ej: pobreza, petróleo…"
                  onChange={(e) => setFilters((p) => ({ ...p, palabrasClave: e.target.value }))}
                />
              </div>

              <div className="dr-field">
                <label>Categorías</label>
                <input
                  value={filters.categorias}
                  placeholder="Separadas por coma (Ej: Economía, Educación)"
                  onChange={(e) => setFilters((p) => ({ ...p, categorias: e.target.value }))}
                />
              </div>

              <div className="dr-field">
                <label>Año desde</label>
                <input
                  value={filters.yearFrom}
                  placeholder="2020"
                  onChange={(e) => setFilters((p) => ({ ...p, yearFrom: e.target.value }))}
                />
              </div>

              <div className="dr-field">
                <label>Año hasta</label>
                <input
                  value={filters.yearTo}
                  placeholder="2025"
                  onChange={(e) => setFilters((p) => ({ ...p, yearTo: e.target.value }))}
                />
              </div>
            </div>

            <div className="dr-advanced-actions">
              <button className="dr-btn" type="button" onClick={handleSearch}>
                Aplicar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* GRID: RESULTADOS + SIDEBAR */}
      <div className="dr-search-grid">
        {/* RESULTADOS */}
        <section className="dr-results">
          <div className="dr-results-meta">
            {meta.total > 0 ? (
              <span>
                Mostrando ítems <strong>{showingFrom}–{showingTo}</strong> de{" "}
                <strong>{meta.total}</strong>
              </span>
            ) : (
              <span className="small">No hay documentos aún</span>
            )}
          </div>

          {meta.total > 0 && !docs.length && (
            <div className="dr-empty">
              No se encontraron documentos que coincidan con la búsqueda.
            </div>
          )}

          {docs.map((d) => {
            const desc = (d.description || d.descripcion || "").toString();
            const snippet = desc.length > 220 ? desc.slice(0, 220) + "…" : desc;

            return (
              <article className="dr-item" key={d.id}>
                <div className="dr-thumb" aria-hidden="true">
                  <div className="dr-thumb-icon">📄</div>
                </div>

                <div className="dr-item-body">
                  <div className="dr-item-title">{d.title}</div>

                  <div className="dr-item-meta">
                    {d.User?.name || "Institución"}
                    {d.entidad ? ` · ${d.entidad}` : ""}
                    {d.anoPublicacion ? ` · ${d.anoPublicacion}` : ""}
                    {d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}
                  </div>

                  {!!snippet && <div className="dr-item-desc">{snippet}</div>}

                  <div className="dr-item-actions">
                    <button
                      className="dr-btn"
                      onClick={() => download(d.id, d.originalname)}
                    >
                      Descargar
                    </button>

                    <div className="dr-rate">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          className="dr-chip"
                          onClick={() => rate(d.id, s)}
                        >
                          {s}★
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="dr-item-file small">
                    {d.originalname || d.filename || ""}
                  </div>
                </div>
              </article>
            );
          })}

          {/* PAGINACIÓN */}
          {meta.total > meta.limit && (
            <div className="dr-pagination">
              <button
                className="dr-chip"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ◀ Anterior
              </button>

              <span className="small">
                Página <strong>{page}</strong> de{" "}
                <strong>{Math.ceil(meta.total / meta.limit)}</strong>
              </span>

              <button
                className="dr-chip"
                disabled={page >= Math.ceil(meta.total / meta.limit)}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente ▶
              </button>
            </div>
          )}
        </section>

        {/* SIDEBAR (UI / futuro) */}
        <aside className="dr-sidebar">
          <div className="dr-box">
            <div className="dr-box-head">LISTAR</div>
            <button className="dr-box-item" type="button">
              Todo DataRegión
            </button>
            <button className="dr-box-item" type="button">
              Comunidades &amp; Colecciones
            </button>
            <button className="dr-box-item" type="button">
              Autores
            </button>
            <button className="dr-box-item" type="button">
              Títulos
            </button>
            <button className="dr-box-item" type="button">
              Por fecha de publicación
            </button>
            <button className="dr-box-item" type="button">
              Palabras clave
            </button>
            <button className="dr-box-item" type="button">
              Temas
            </button>
          </div>

          <div className="dr-box">
            <div className="dr-box-head">DESCUBRE</div>
            <button className="dr-box-item" type="button">
              Autor / Institución
            </button>
            <button className="dr-box-item" type="button">
              Palabras clave
            </button>
            <button className="dr-box-item" type="button">
              Entidad
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
